import { isTauri } from "@/utils";
import type { OllamaStatus } from "@/types";
import { pingVersion } from "./providers/ollama/ollamaClient";

/**
 * Ollama lifecycle — installation/running detection and startup.
 *
 * Backed by native Tauri commands (`ollama_status` / `ollama_start`, see
 * `src-tauri/src/ollama.rs`) inside the app shell. Falls back to a plain HTTP
 * ping when running as a browser dev server (`pnpm dev` without Tauri), where
 * filesystem/process checks aren't available — "installed" is then reported
 * as true iff the service answers.
 */
export async function checkOllamaStatus(baseUrl: string): Promise<OllamaStatus> {
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    return invoke<OllamaStatus>("ollama_status");
  }
  const version = await pingVersion(baseUrl);
  // Outside Tauri there's no filesystem/process access to check "installed"
  // independently, so an unreachable server is reported as "not running"
  // rather than "not installed" — the honest answer given what we can see.
  return { installed: true, running: version !== null, version };
}

export async function startOllama(): Promise<void> {
  if (!isTauri()) {
    throw new Error("Starting Ollama automatically is only available in the desktop app.");
  }
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke<void>("ollama_start");
}
